import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { fetchTrendStory } from '../service';
import type { TrendQualityScope } from '../service';
import type { TrendStory } from '../types';
import { extractTrendUiErrorMessage } from '../errorUtils';
import { filterMeaningfulTrendStrings, getMeaningfulTrendText } from '../contentQuality';
import TrendArticleDetailDialog from './TrendArticleDetailDialog';
import {
  DISPLAY_FONT,
  HEADING_SX,
  SECTION_HEADING_SX,
  TREND_COLORS,
  getArticleImage,
  getDesignSubCategoryLabel,
  getFallbackArticleImage,
} from '../trendTheme';

type TrendStoryDetailDialogProps = {
  storyId: string | null;
  open: boolean;
  onClose: () => void;
  qualityScope?: TrendQualityScope;
};

function MetaChip({ label }: { label: string }) {
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.68rem', borderColor: TREND_COLORS.divider, color: TREND_COLORS.textMuted }}
    />
  );
}

function getSupportingArticleQualityLabel(status: TrendStory['supportingArticles'][number]['qualityStatus']): string {
  if (status === 'requires_review') return 'Kräver granskning';
  if (status === 'blocked') return 'Blockerad';
  return 'Publicerbar';
}

function summarizeSourceDates(references: Array<{ publishedAt?: string | null }>) {
  const dated = references
    .map((reference) => reference.publishedAt || null)
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => right.localeCompare(left));

  return {
    newest: dated[0] ? dated[0].slice(0, 10) : null,
    oldest: dated[dated.length - 1] ? dated[dated.length - 1].slice(0, 10) : null,
  };
}

function formatSupportingArticleDate(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export default function TrendStoryDetailDialog({
  storyId,
  open,
  onClose,
  qualityScope = 'publishable',
}: TrendStoryDetailDialogProps) {
  const [story, setStory] = useState<TrendStory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !storyId) {
      setStory(null);
      setError(null);
      setSelectedArticleId(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const next = await fetchTrendStory(storyId, qualityScope);
        if (!cancelled) setStory(next);
      } catch (err: any) {
        if (!cancelled) {
          setError(extractTrendUiErrorMessage(err, 'Kunde inte ladda berättelsen.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [open, qualityScope, storyId]);

  const imageUrl = story ? getArticleImage(story.heroImage, story.category) : '';
  const fallbackImageUrl = getFallbackArticleImage(story?.category);
  const storyBody = story?.body || story?.whatItMeansForUs || story?.summary || '';
  const sourceDateSummary = summarizeSourceDates(story?.sourceReferences || []);
  const storyCategory = String(story?.category || story?.storyType || '').trim().toLowerCase();
  const isFoodStory = storyCategory === 'food' || storyCategory === 'consumer';
  const storyMeaning = getMeaningfulTrendText(story?.whatItMeansForUs || '', { minLength: 80 });
  const storyDvh = getMeaningfulTrendText(story?.dvhAngle || '', { minLength: 60 });
  const storyFoodservice = getMeaningfulTrendText(story?.foodserviceAngle || '', { minLength: 60 });
  const storyProductConnections = filterMeaningfulTrendStrings(story?.productConnections || [], 'product');
  const storyRecipeConnections = filterMeaningfulTrendStrings(story?.recipeConnections || [], 'recipe');
  const storyRecommendedActions = filterMeaningfulTrendStrings(story?.recommendedActions || [], 'action');
  const hasAudienceSignals = Boolean(story?.audienceSegments.length || story?.consumerBehaviors.length);
  const hasUsageSignals = Boolean(story?.usageOccasions.length || story?.designSignals.length);
  const hasChannelAngles = Boolean(storyDvh || storyFoodservice);
  const hasNextSteps = storyRecommendedActions.length > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: TREND_COLORS.pageBg,
            maxHeight: '92vh',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: TREND_COLORS.navy }} />
            </Box>
          )}
          {error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}

          {!loading && !error && story && (
            <Stack>
              <Box sx={{ position: 'relative', pt: '42%', overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={imageUrl}
                  alt={story.title}
                  onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                    if (event.currentTarget.src !== fallbackImageUrl) {
                      event.currentTarget.src = fallbackImageUrl;
                    }
                  }}
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(7,47,83,0.9) 0%, rgba(7,47,83,0.2) 65%)',
                  }}
                />
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 3, md: 4 } }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip
                      label={(story.storyType || story.category || 'berättelse').toUpperCase()}
                      size="small"
                      sx={{ bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, fontWeight: 800, fontSize: '0.62rem', letterSpacing: '0.1em' }}
                    />
                    {story.designSubCategory && (
                      <Chip
                        label={getDesignSubCategoryLabel(story.designSubCategory)}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700 }}
                      />
                    )}
                    <Chip
                      label={`${story.articleCount} artiklar`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700 }}
                    />
                  </Stack>
                  <Typography
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      color: '#fff',
                      lineHeight: 1.1,
                      fontSize: { xs: '1.45rem', md: '1.9rem' },
                    }}
                  >
                    {story.title}
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={0} sx={{ px: { xs: 3, md: 4 }, py: 3 }}>
                {!story.publicationQuality.passes && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Blockerad från publika ytor
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                      {story.publicationQuality.reasons.map((reason) => (
                        <Typography component="li" key={reason} variant="body2">
                          {reason}
                        </Typography>
                      ))}
                    </Stack>
                  </Alert>
                )}

                <Typography
                  variant="body1"
                  sx={{
                    color: TREND_COLORS.textSecondary,
                    lineHeight: 1.85,
                    fontSize: '1.05rem',
                    mb: 3,
                    borderLeft: `3px solid ${TREND_COLORS.gold}`,
                    pl: 2.5,
                  }}
                >
                  {story.summary || story.whatItMeansForUs}
                </Typography>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                  {story.signalLabels.slice(0, 6).map((entry) => (
                    <MetaChip key={entry} label={entry} />
                  ))}
                </Stack>

                <Divider sx={{ borderColor: TREND_COLORS.divider, mb: 3 }} />

                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>FÖRDJUPNING</Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.9, whiteSpace: 'pre-line' }}
                  >
                    {storyBody || 'Ingen fördjupning tillgänglig än.'}
                  </Typography>
                </Box>

                {storyMeaning && (
                  <Box sx={{ mb: 4 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>VAD BETYDER DETTA FÖR FONTANA?</Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.85, whiteSpace: 'pre-line' }}
                    >
                      {storyMeaning}
                    </Typography>
                  </Box>
                )}

                {(hasAudienceSignals || hasUsageSignals) && (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {hasAudienceSignals && (
                      <Grid size={{ xs: 12, md: hasUsageSignals ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Typography sx={{ ...HEADING_SX, fontSize: '0.82rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                            MÅLGRUPPER OCH BETEENDEN
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: story.consumerBehaviors.length > 0 ? 1.5 : 0 }}>
                            {story.audienceSegments.map((entry) => <MetaChip key={entry} label={entry} />)}
                          </Stack>
                          {story.consumerBehaviors.length > 0 && (
                            <Stack spacing={1}>
                              {story.consumerBehaviors.map((entry) => (
                                <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                                  {entry}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    )}
                    {hasUsageSignals && (
                      <Grid size={{ xs: 12, md: hasAudienceSignals ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Typography sx={{ ...HEADING_SX, fontSize: '0.82rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                            ANVÄNDNINGSTILLFÄLLEN OCH DESIGNSPÅR
                          </Typography>
                          {story.usageOccasions.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: story.designSignals.length > 0 ? 1.5 : 0 }}>
                              {story.usageOccasions.map((entry) => <MetaChip key={entry} label={entry} />)}
                            </Stack>
                          )}
                          {story.designSignals.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {story.designSignals.map((entry) => <MetaChip key={entry} label={entry} />)}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}

                {hasChannelAngles && (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {storyDvh && (
                      <Grid size={{ xs: 12, md: storyFoodservice ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Typography sx={{ ...HEADING_SX, fontSize: '0.82rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                            DVH
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                            {storyDvh}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {storyFoodservice && (
                      <Grid size={{ xs: 12, md: storyDvh ? 6 : 12 }}>
                        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                          <Typography sx={{ ...HEADING_SX, fontSize: '0.82rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                            FOODSERVICE
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                            {storyFoodservice}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}

                {(storyProductConnections.length > 0 || (isFoodStory && storyRecipeConnections.length > 0)) && (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {storyProductConnections.length > 0 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>PRODUKTSPÅR</Typography>
                        <Stack spacing={1}>
                          {storyProductConnections.map((entry) => (
                            <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, pl: 2, borderLeft: `2px solid ${TREND_COLORS.olive}` }}>
                              {entry}
                            </Typography>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                    {isFoodStory && storyRecipeConnections.length > 0 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>RECEPTSPÅR</Typography>
                        <Stack spacing={1}>
                          {storyRecipeConnections.map((entry) => (
                            <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, pl: 2, borderLeft: `2px solid ${TREND_COLORS.terracotta}` }}>
                              {entry}
                            </Typography>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                )}

                {hasNextSteps && (
                  <Box sx={{ mb: 4 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>NÄSTA STEG</Typography>
                    <Stack spacing={1}>
                      {storyRecommendedActions.map((entry) => (
                        <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75 }}>
                          {entry}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>ARTIKLAR SOM BYGGER STORYN</Typography>
                  <Stack spacing={1.5}>
                    {story.supportingArticles.length > 0 ? story.supportingArticles.map((article) => (
                      <ButtonBase
                        key={article._id}
                        onClick={() => setSelectedArticleId(article._id)}
                        sx={{
                          display: 'block',
                          textAlign: 'left',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                            <Chip
                              size="small"
                              label={getSupportingArticleQualityLabel(article.qualityStatus)}
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={(article.category || 'artikel').toUpperCase()}
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Stack>
                          {(article.sourceName || article.publishedAt) && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                              {article.sourceName && (
                                article.sourceUrl ? (
                                  <Link
                                    href={article.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ color: TREND_COLORS.olive, fontSize: '0.72rem', fontWeight: 700 }}
                                  >
                                    {article.sourceName}
                                  </Link>
                                ) : (
                                  <Typography variant="caption" sx={{ color: TREND_COLORS.olive, fontWeight: 700 }}>
                                    {article.sourceName}
                                  </Typography>
                                )
                              )}
                              {article.publishedAt && (
                                <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                                  {formatSupportingArticleDate(article.publishedAt)}
                                </Typography>
                              )}
                            </Stack>
                          )}
                          <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>
                            {article.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.7, mb: article.excerpt ? 0.75 : 0 }}>
                            {article.supportNote || article.excerpt}
                          </Typography>
                          {article.excerpt && article.excerpt !== article.supportNote && (
                            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                              {article.excerpt}
                            </Typography>
                          )}
                          {!article.publicationQuality.passes && (
                            <Typography variant="caption" sx={{ color: '#8A5D00', display: 'block', mt: 0.75 }}>
                              {article.publicationQuality.reasons.join(' ')}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: TREND_COLORS.olive, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mt: 1, display: 'block' }}>
                            Läs hela artikeln
                          </Typography>
                        </Box>
                      </ButtonBase>
                    )) : (
                      <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Inga artiklar som bygger storyn är tillgängliga än.</Typography>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>KÄLLOR</Typography>
                  {(sourceDateSummary.newest || sourceDateSummary.oldest) && (
                    <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mb: 1.5 }}>
                      {[
                        sourceDateSummary.newest ? `Senaste källa: ${sourceDateSummary.newest}` : '',
                        sourceDateSummary.oldest && sourceDateSummary.oldest !== sourceDateSummary.newest
                          ? `Äldsta källa: ${sourceDateSummary.oldest}`
                          : '',
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  <Stack spacing={1.5}>
                    {story.sourceReferences.length > 0 ? story.sourceReferences.map((reference) => (
                      <Stack key={`${reference.title}-${reference.url}`} spacing={0.3}>
                        <Link
                          href={reference.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.85rem' }}
                        >
                          {reference.title || reference.url}
                        </Link>
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                          {[reference.sourceName, reference.publishedAt?.slice(0, 10), reference.note]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Stack>
                    )) : (
                      <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Inga källreferenser än.</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: TREND_COLORS.pageBg }}>
          <Button onClick={onClose} sx={{ ...HEADING_SX, fontSize: '0.8rem', color: TREND_COLORS.navy }}>
            Stäng
          </Button>
        </DialogActions>
      </Dialog>
      <TrendArticleDetailDialog
        articleId={selectedArticleId}
        open={!!selectedArticleId}
        onClose={() => setSelectedArticleId(null)}
        qualityScope={qualityScope}
      />
    </>
  );
}

